/** Pan/zoom viewport for the floor plan canvas (screen-space pan, uniform scale). */

export type FloorViewport = {
  scale: number
  panX: number
  panY: number
}

export const DEFAULT_FLOOR_VIEWPORT: FloorViewport = { scale: 1, panX: 0, panY: 0 }

export const FLOOR_ZOOM_MIN = 0.35
export const FLOOR_ZOOM_MAX = 4

export function clampFloorZoom(scale: number): number {
  return Math.min(FLOOR_ZOOM_MAX, Math.max(FLOOR_ZOOM_MIN, scale))
}

export function applyFloorViewport(ctx: CanvasRenderingContext2D, viewport: FloorViewport): void {
  ctx.translate(viewport.panX, viewport.panY)
  ctx.scale(viewport.scale, viewport.scale)
}

/** Canvas element coordinates (0..width, 0..height) from client pointer. */
export function clientToCanvasPoint(
  clientX: number,
  clientY: number,
  canvasRect: DOMRect,
): { x: number; y: number } {
  return { x: clientX - canvasRect.left, y: clientY - canvasRect.top }
}

/** Inverse viewport transform — world coords used by draw/hit-test layout. */
export function canvasPointToWorld(
  canvasX: number,
  canvasY: number,
  viewport: FloorViewport,
): { x: number; y: number } {
  return {
    x: (canvasX - viewport.panX) / viewport.scale,
    y: (canvasY - viewport.panY) / viewport.scale,
  }
}

export function clientToWorldCanvas(
  clientX: number,
  clientY: number,
  canvasRect: DOMRect,
  viewport: FloorViewport,
): { x: number; y: number } {
  const p = clientToCanvasPoint(clientX, clientY, canvasRect)
  return canvasPointToWorld(p.x, p.y, viewport)
}

export function worldCanvasToClient(
  worldX: number,
  worldY: number,
  canvasRect: DOMRect,
  viewport: FloorViewport,
): { x: number; y: number } {
  return {
    x: canvasRect.left + viewport.panX + viewport.scale * worldX,
    y: canvasRect.top + viewport.panY + viewport.scale * worldY,
  }
}

export function zoomViewportAtCanvasPoint(
  viewport: FloorViewport,
  canvasX: number,
  canvasY: number,
  factor: number,
): FloorViewport {
  const nextScale = clampFloorZoom(viewport.scale * factor)
  if (nextScale === viewport.scale) return viewport
  const ratio = nextScale / viewport.scale
  return {
    scale: nextScale,
    panX: canvasX - (canvasX - viewport.panX) * ratio,
    panY: canvasY - (canvasY - viewport.panY) * ratio,
  }
}

export function floorViewportChanged(viewport: FloorViewport): boolean {
  return (
    viewport.scale !== 1 ||
    Math.abs(viewport.panX) > 0.5 ||
    Math.abs(viewport.panY) > 0.5
  )
}

/** 100% zoom — native floor pixel size, centered in the canvas. */
export function resetFloorViewportToNative(
  canvasW: number,
  canvasH: number,
  layout: { x: number; y: number; w: number; h: number },
): FloorViewport {
  if (canvasW < 1 || canvasH < 1) return DEFAULT_FLOOR_VIEWPORT
  const cx = layout.x + layout.w / 2
  const cy = layout.y + layout.h / 2
  return {
    scale: 1,
    panX: canvasW / 2 - cx,
    panY: canvasH / 2 - cy,
  }
}

/** Scale and pan so the floor draw rect fills the canvas (reduces pillarboxing on wide layouts). */
export function fitFloorViewportToLayout(
  canvasW: number,
  canvasH: number,
  layout: { x: number; y: number; w: number; h: number },
  margin = 0.94,
): FloorViewport {
  if (layout.w < 1 || layout.h < 1 || canvasW < 1 || canvasH < 1) {
    return DEFAULT_FLOOR_VIEWPORT
  }
  const scale = clampFloorZoom(
    Math.min((canvasW * margin) / layout.w, (canvasH * margin) / layout.h),
  )
  const cx = layout.x + layout.w / 2
  const cy = layout.y + layout.h / 2
  return {
    scale,
    panX: canvasW / 2 - scale * cx,
    panY: canvasH / 2 - scale * cy,
  }
}

export function floorViewportAtBaseline(
  viewport: FloorViewport,
  baseline: FloorViewport,
  tolerance = 0.5,
): boolean {
  const scaleTol = Math.max(0.002, baseline.scale * 0.002)
  return (
    Math.abs(viewport.scale - baseline.scale) <= scaleTol &&
    Math.abs(viewport.panX - baseline.panX) <= tolerance &&
    Math.abs(viewport.panY - baseline.panY) <= tolerance
  )
}

/** User-facing zoom % relative to the current reset/fit baseline (always 100% at baseline). */
export function viewportZoomPercent(viewport: FloorViewport, baseline: FloorViewport): number {
  if (floorViewportAtBaseline(viewport, baseline)) return 100
  const base = baseline.scale > 0 ? baseline.scale : 1
  return Math.round((viewport.scale / base) * 100)
}
